const TOKEN_CLEAR_LOG_TYPE = 'TOKEN_CLEAR'

let configuredReporter = null

function getBrowserStorage() {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage
  } catch (error) {
    return null
  }
}

function getBrowserPageStorage() {
  try {
    return typeof sessionStorage === 'undefined' ? null : sessionStorage
  } catch (error) {
    return null
  }
}

function safeJsonParse(value) {
  if (!value) return {}
  try {
    const result = JSON.parse(value)
    return result && typeof result === 'object' ? result : {}
  } catch (error) {
    return {}
  }
}

function getFirstValue(source, keys) {
  for (const key of keys) {
    if (source && source[key] !== undefined && source[key] !== null && source[key] !== '') {
      return source[key]
    }
  }
  return ''
}

function stripQuery(value) {
  const text = String(value || '').trim()
  const queryIndex = text.indexOf('?')
  return queryIndex === -1 ? text : text.slice(0, queryIndex)
}

function redactText(value, tokenValue = '') {
  let text = String(value || '')
  if (tokenValue) {
    text = text.split(tokenValue).join('[REDACTED_TOKEN]')
  }
  const hasSensitiveAssignment = /(?:token(?:Value|_value|-value)?|access(?:Token|_token|-token)?|refresh(?:Token|_token|-token)?|password|passwd|authorization)(?:\\?["']|%22)?\s*(?::|=|%3A|%3D)/i.test(text)
  const hasBearerCredential = /\bBearer(?:\s|%20)+[A-Za-z0-9._~+/=%-]+/i.test(text)
  if (hasSensitiveAssignment || hasBearerCredential) {
    return '[REDACTED_SENSITIVE_TEXT]'
  }
  return text.slice(0, 1500)
}

function redactScalar(value, tokenValue) {
  return typeof value === 'number' || typeof value === 'boolean'
    ? value
    : redactText(value, tokenValue)
}

function getFirstNonNullValue(values, fallbackValue) {
  for (const value of values) {
    if (value !== undefined && value !== null) {
      return value
    }
  }
  return fallbackValue
}

function getStoredValue(source, key, fallbackValue = '') {
  return source && Object.prototype.hasOwnProperty.call(source, key)
    ? source[key]
    : fallbackValue
}

function getEstimatedExpireAt(capturedAt, tokenTimeout) {
  if (!capturedAt || tokenTimeout === '' || tokenTimeout === null || tokenTimeout === undefined) {
    return ''
  }
  const capturedAtTimestamp = Date.parse(capturedAt)
  const timeoutSeconds = Number(tokenTimeout)
  if (!Number.isFinite(capturedAtTimestamp) || !Number.isFinite(timeoutSeconds) || timeoutSeconds < 0) {
    return ''
  }
  return new Date(capturedAtTimestamp + timeoutSeconds * 1000).toISOString()
}

function normalizeError(error, tokenValue) {
  if (!error) return null
  return {
    name: redactText(error.name || '', tokenValue),
    message: redactText(error.msg || error.message || '', tokenValue),
    code: redactScalar(getFirstNonNullValue([error.code, error.errorCode], ''), tokenValue),
    status: redactScalar(getFirstNonNullValue([
      error.status,
      error.response && error.response.status,
    ], ''), tokenValue),
  }
}

function reportConsoleError(logger, message, error, tokenValue) {
  try {
    logger.error(message, normalizeError(error, tokenValue))
  } catch (consoleError) {
    // 诊断日志本身不能影响登录状态清理。
  }
}

function getDefaultEnvironment() {
  const browserNavigator = typeof navigator === 'undefined' ? null : navigator
  const browserDocument = typeof document === 'undefined' ? null : document
  const browserLocation = typeof location === 'undefined' ? null : location
  return {
    online: browserNavigator ? browserNavigator.onLine : null,
    visibilityState: browserDocument ? browserDocument.visibilityState : '',
    userAgent: browserNavigator ? browserNavigator.userAgent : '',
    pagePath: browserLocation ? `${browserLocation.pathname || ''}${browserLocation.hash || ''}` : '',
  }
}

function createCallStack(tokenValue) {
  try {
    return redactText(new Error('token clear trace').stack || '', tokenValue)
      .split('\n')
      .slice(1, 9)
      .join('\n')
  } catch (error) {
    return ''
  }
}

export function configureTokenClearReporter(reporter) {
  configuredReporter = typeof reporter === 'function' ? reporter : null
}

export function storeLoginTokenInfo(user, options = {}) {
  const storage = options.storage || getBrowserStorage()
  if (!storage) return false

  const now = options.now || (() => new Date())
  const normalizedUser = user && typeof user === 'object' ? user : {}
  const saTokenInfo = normalizedUser.saTokenInfo && typeof normalizedUser.saTokenInfo === 'object'
    ? normalizedUser.saTokenInfo
    : {}

  storage.setItem('tokenName', String(saTokenInfo.tokenName || ''))
  storage.setItem('tokenValue', String(saTokenInfo.tokenValue || ''))
  storage.setItem('userInfo', JSON.stringify(normalizedUser))
  storage.setItem('tokenInfoCapturedAt', now().toISOString())
  return true
}

export function readStoredLoginContext(storageRef) {
  const storage = storageRef || getBrowserStorage()
  const invalidKeys = []
  const result = {
    tokenName: storage ? String(storage.getItem('tokenName') || '').trim() : '',
    tokenValue: storage ? String(storage.getItem('tokenValue') || '').trim() : '',
  }

  if (!result.tokenName) invalidKeys.push('tokenName')
  if (!result.tokenValue) invalidKeys.push('tokenValue')

  for (const key of ['userInfo', 'companyInfo', 'sysParam', 'functionPowers']) {
    const value = storage ? storage.getItem(key) : ''
    try {
      const parsedValue = value ? JSON.parse(value) : null
      const isObject = !!parsedValue && typeof parsedValue === 'object' && !Array.isArray(parsedValue)
      let isValid = isObject
      if (key === 'userInfo') {
        isValid = isObject && !!getFirstValue(parsedValue, ['id', 'eomsUserId', 'userId'])
      } else if (key === 'companyInfo') {
        isValid = isObject && !!getFirstValue(parsedValue, ['companyId', 'id'])
      } else if (key === 'sysParam') {
        isValid = Array.isArray(parsedValue)
      } else if (key === 'functionPowers') {
        isValid = Array.isArray(parsedValue) && parsedValue.length > 0
      }
      if (isValid) {
        result[key] = parsedValue
      } else {
        invalidKeys.push(key)
      }
    } catch (error) {
      invalidKeys.push(key)
    }
  }

  const companyUserValue = storage ? storage.getItem('companyUser') : ''
  if (companyUserValue) {
    try {
      const companyUser = JSON.parse(companyUserValue)
      if (companyUser && typeof companyUser === 'object' && !Array.isArray(companyUser)) {
        result.companyUser = companyUser
      }
    } catch (error) {
      result.companyUser = {}
    }
  }

  if (invalidKeys.length > 0) {
    const error = new Error(`login cache is incomplete or invalid: ${invalidKeys.join(', ')}`)
    error.code = 'LOGIN_CACHE_INVALID'
    error.invalidKeys = invalidKeys
    throw error
  }

  return result
}

export function buildTokenClearLog(reasonCode, context = {}, options = {}) {
  const storage = options.storage || getBrowserStorage()
  const pageStorage = options.pageStorage || getBrowserPageStorage() || storage
  const environment = options.environment || getDefaultEnvironment()
  const now = options.now || (() => new Date())
  const tokenName = storage ? String(storage.getItem('tokenName') || '') : ''
  const tokenValue = storage ? String(storage.getItem('tokenValue') || '') : ''
  const userInfo = storage ? safeJsonParse(storage.getItem('userInfo')) : {}
  const companyInfo = storage ? safeJsonParse(storage.getItem('companyInfo')) : {}
  const saTokenInfo = userInfo.saTokenInfo && typeof userInfo.saTokenInfo === 'object'
    ? userInfo.saTokenInfo
    : {}
  const tokenInfoCapturedAt = storage ? String(storage.getItem('tokenInfoCapturedAt') || '') : ''
  const tokenTimeout = getStoredValue(saTokenInfo, 'tokenTimeout')

  return {
    logType: TOKEN_CLEAR_LOG_TYPE,
    logVersion: 1,
    occurredAt: now().toISOString(),
    reason: {
      code: String(reasonCode || 'UNKNOWN_TOKEN_CLEAR'),
      source: redactText(context.source || '', tokenValue),
      message: redactText(context.reasonMessage || '', tokenValue),
    },
    trigger: {
      apiUrl: stripQuery(context.apiUrl),
      apiMethod: String(context.apiMethod || '').toUpperCase(),
      businessCode: redactScalar(getFirstNonNullValue([context.businessCode], ''), tokenValue),
      returnCode: redactScalar(getFirstNonNullValue([context.returnCode], ''), tokenValue),
      httpStatus: redactScalar(getFirstNonNullValue([context.httpStatus], ''), tokenValue),
      error: normalizeError(context.error, tokenValue),
    },
    page: {
      routeName: redactText(context.routeName || '', tokenValue),
      routePath: stripQuery(context.routePath),
      fromRouteName: redactText(context.fromRouteName || '', tokenValue),
      fromRoutePath: stripQuery(context.fromRoutePath),
      pagePath: stripQuery(environment.pagePath),
      is404: context.is404 === true,
      hasLoginBridgeData: context.hasLoginBridgeData === true,
    },
    session: {
      userId: getFirstValue(userInfo, ['id', 'eomsUserId', 'userId']),
      userName: redactText(getFirstValue(userInfo, ['userName', 'nickName', 'name']), tokenValue),
      companyId: getFirstValue(companyInfo, ['companyId', 'id']),
      companyName: redactText(getFirstValue(companyInfo, ['companyName', 'companySimpleName', 'name']), tokenValue),
      shopId: storage ? String(storage.getItem('currShopId') || '') : '',
      activePageUUID: pageStorage ? String(pageStorage.getItem('activePageUUID') || '') : '',
    },
    auth: {
      tokenName: redactText(tokenName, tokenValue),
      tokenValue,
      tokenPresent: !!tokenValue,
      tokenLength: tokenValue.length,
      isLogin: getStoredValue(saTokenInfo, 'isLogin'),
      loginDevice: getStoredValue(saTokenInfo, 'loginDevice'),
      loginId: getStoredValue(saTokenInfo, 'loginId'),
      loginType: getStoredValue(saTokenInfo, 'loginType'),
      sessionTimeout: getStoredValue(saTokenInfo, 'sessionTimeout'),
      tag: getStoredValue(saTokenInfo, 'tag'),
      tokenActivityTimeout: getStoredValue(saTokenInfo, 'tokenActivityTimeout'),
      tokenSessionTimeout: getStoredValue(saTokenInfo, 'tokenSessionTimeout'),
      tokenTimeout,
      tokenInfoCapturedAt,
      tokenEstimatedExpireAt: getEstimatedExpireAt(tokenInfoCapturedAt, tokenTimeout),
      lastLoginTime: getStoredValue(userInfo, 'lastLoginTime'),
    },
    runtime: {
      online: getFirstNonNullValue([environment.online], null),
      visibilityState: String(environment.visibilityState || ''),
      userAgent: redactText(environment.userAgent || '', tokenValue),
    },
    callStack: createCallStack(tokenValue),
  }
}

export function clearTokenValueWithLog(reasonCode, context = {}, options = {}) {
  const storage = options.storage || getBrowserStorage()
  if (!storage) return {cleared: false, reportDispatched: false}

  const tokenName = String(storage.getItem('tokenName') || '')
  const tokenValue = String(storage.getItem('tokenValue') || '')
  if (!tokenValue) {
    storage.setItem('tokenValue', '')
    return {cleared: false, reportDispatched: false}
  }

  const reporter = options.reporter || configuredReporter
  const logger = options.logger || console
  let reportDispatched = false

  try {
    const payload = buildTokenClearLog(reasonCode, context, {...options, storage})
    if (reporter) {
      const reportResult = reporter(payload, {tokenName, tokenValue})
      reportDispatched = true
      if (reportResult && typeof reportResult.catch === 'function') {
        reportResult.catch(error => reportConsoleError(
          logger,
          'token clear log report failed',
          error,
          tokenValue
        ))
      }
    }
  } catch (error) {
    reportConsoleError(logger, 'token clear log dispatch failed', error, tokenValue)
  } finally {
    storage.setItem('tokenValue', '')
  }

  return {cleared: true, reportDispatched}
}

export function clearStoredLoginSessionWithLog(reasonCode, context = {}, options = {}) {
  const storage = options.storage || getBrowserStorage()
  const clearResult = clearTokenValueWithLog(reasonCode, context, {...options, storage})
  if (!storage) return clearResult

  for (const key of [
    'tokenName',
    'userInfo',
    'companyInfo',
    'companyUser',
    'sysParam',
    'functionPowers',
    'tokenInfoCapturedAt',
    'currShopId',
    'routerName',
  ]) {
    storage.setItem(key, '')
  }
  return clearResult
}
